import { useState } from "react";
import { useItemService } from "@/hooks/useServices";
import { logger } from "@/lib/logger";
import { buildExportFilename, downloadXlsx, XlsxColumn } from "@/lib/xlsx";
import { Pagination } from "@/models/base/Pagination";
import { Group } from "@/models/feature/feature";
import { InvitedUser, User, UserWithRole } from "@/models/users/User";
import Workspace from "@/models/workspace/Workspace";
import type RestResponse from "@/models/client/RestResponse";
import type {
  InvitedUserSortBy,
  UserSortBy,
} from "@/services/userManagementService";
import type { OrphanedWorkspaceSortBy } from "@/services/workspaceServices";
import { GroupSortBy, sortGroups } from "../utils/sortGroups";
import {
  groupExportColumns,
  invitedUserExportColumns,
  orphanedWorkspaceExportColumns,
  toGroupMemberRows,
  toUserExportRows,
  userExportColumns,
} from "../utils/exportSheets";
import { restError } from "@/models/client/RestResponse";

/** Big enough that one request covers any realistic tenant; the loop below covers the rest. */
const EXPORT_PAGE_SIZE = 500;

/** Guards against an unbounded loop if the API ever reports an inconsistent total. */
const MAX_EXPORT_PAGES = 100;

/**
 * Walks every page of a paginated endpoint. Exports must cover the whole result set —
 * exporting only the page on screen would quietly drop most of the rows.
 */
const fetchAllPages = async <T>(
  fetchPage: (pageNumber: number) => RestResponse<Pagination<T>>,
  operation: string
): Promise<T[]> => {
  const items: T[] = [];

  for (let pageNumber = 1; pageNumber <= MAX_EXPORT_PAGES; pageNumber++) {
    const response = await fetchPage(pageNumber);
    if (!response.success) throw restError(response);

    items.push(...response.value.data);
    if (!response.value.hasMore) return items;
  }

  logger.warn("Export stopped at the page limit", {
    operation,
    exportedRows: items.length,
  });
  return items;
};

interface UsersExportOptions {
  /** Restricts the export to the rows matching the table's active search. */
  search?: string;
  sortBy: UserSortBy;
  sortAscending: boolean;
  tenantName?: string;
}

interface InvitedUsersExportOptions {
  sortBy: InvitedUserSortBy;
  sortAscending: boolean;
  tenantName?: string;
}

interface GroupsExportOptions {
  search?: string;
  sortBy: GroupSortBy;
  sortAscending: boolean;
  tenantName?: string;
}

interface OrphanedWorkspacesExportOptions {
  search?: string;
  sortBy: OrphanedWorkspaceSortBy;
  sortAscending: boolean;
  tenantName?: string;
}

/**
 * Exports each user-management tab to a real .xlsx workbook.
 *
 * Every export covers all pages and honours the tab's active search, so the sheet matches
 * what the admin is looking at rather than just the visible page.
 */
const useUserManagementExport = () => {
  const userManagementService = useItemService(
    (services) => services.userManagement
  );
  const groupService = useItemService((services) => services.group);
  const userService = useItemService((services) => services.user);
  const workspaceService = useItemService((services) => services.workspace);
  const [isExporting, setIsExporting] = useState(false);

  /** Groups need a separate permission, so failure degrades the sheet instead of failing it. */
  const fetchGroupsOrEmpty = async (operation: string): Promise<Group[]> => {
    try {
      const response = await groupService.getGroups();
      if (response.success) return response.value;

      logger.warn("Export could not read groups", {
        operation,
        reason: response.feedback.message,
      });
      return [];
    } catch (error) {
      logger.error("Export could not read groups", { operation, error });
      return [];
    }
  };

  /** Wraps an export with the spinner, logging and the error contract the callers rely on. */
  const runExport = async <T>(
    operation: string,
    filenamePrefix: string,
    sheetName: string,
    tenantName: string | undefined,
    columns: XlsxColumn<T>[],
    loadRows: () => Promise<T[]>
  ): Promise<number> => {
    setIsExporting(true);
    try {
      const rows = await loadRows();

      await downloadXlsx(
        buildExportFilename(filenamePrefix, tenantName, new Date()),
        sheetName,
        columns,
        rows
      );

      logger.info("Exported user management sheet", {
        operation,
        exportedRows: rows.length,
      });
      return rows.length;
    } catch (error) {
      logger.error("Failed to export user management sheet", {
        operation,
        error,
      });
      throw error;
    } finally {
      setIsExporting(false);
    }
  };

  const exportUsers = ({
    search,
    sortBy,
    sortAscending,
    tenantName,
  }: UsersExportOptions): Promise<number> =>
    runExport(
      "exportUsers",
      "brugere",
      "Brugere",
      tenantName,
      userExportColumns,
      async () => {
        const [users, groups] = await Promise.all([
          fetchAllPages<UserWithRole>(
            (pageNumber) =>
              userManagementService.getUsersWithRoles(
                pageNumber,
                EXPORT_PAGE_SIZE,
                search,
                sortBy,
                sortAscending
              ),
            "exportUsers"
          ),
          fetchGroupsOrEmpty("exportUsers"),
        ]);

        return toUserExportRows(users, groups);
      }
    );

  const exportInvitedUsers = ({
    sortBy,
    sortAscending,
    tenantName,
  }: InvitedUsersExportOptions): Promise<number> =>
    runExport(
      "exportInvitedUsers",
      "inviterede-brugere",
      "Inviterede brugere",
      tenantName,
      invitedUserExportColumns(new Date()),
      () =>
        fetchAllPages<InvitedUser>(
          (pageNumber) =>
            userManagementService.getInvitedUsers(
              pageNumber,
              EXPORT_PAGE_SIZE,
              sortBy,
              sortAscending
            ),
          "exportInvitedUsers"
        )
    );

  const exportGroups = ({
    search,
    sortBy,
    sortAscending,
    tenantName,
  }: GroupsExportOptions): Promise<number> =>
    runExport(
      "exportGroups",
      "grupper",
      "Grupper",
      tenantName,
      groupExportColumns,
      async () => {
        const [groupsResponse, usersResponse] = await Promise.all([
          groupService.getGroups(),
          userService.getAllTenantUser(),
        ]);
        if (!groupsResponse.success) throw restError(groupsResponse);

        // Member names are cosmetic; an unreadable user list still yields a usable sheet.
        if (!usersResponse.success)
          logger.warn("Group export could not read tenant users", {
            operation: "exportGroups",
            reason: usersResponse.feedback.message,
          });
        const users: User[] = usersResponse.success ? usersResponse.value : [];
        const searchLower = search?.toLowerCase();
        const groups = searchLower
          ? groupsResponse.value.filter((group) =>
              group.name.toLowerCase().includes(searchLower)
            )
          : groupsResponse.value;

        return toGroupMemberRows(
          sortGroups(groups, sortBy, sortAscending),
          users
        );
      }
    );

  const exportOrphanedWorkspaces = ({
    search,
    sortBy,
    sortAscending,
    tenantName,
  }: OrphanedWorkspacesExportOptions): Promise<number> =>
    runExport(
      "exportOrphanedWorkspaces",
      "foraeldreloese-workspaces",
      "Forældreløse workspaces",
      tenantName,
      orphanedWorkspaceExportColumns,
      () =>
        fetchAllPages<Workspace>(
          (pageNumber) =>
            workspaceService.getOrphanedWorkspaces(
              pageNumber,
              EXPORT_PAGE_SIZE,
              search,
              sortBy,
              sortAscending
            ),
          "exportOrphanedWorkspaces"
        )
    );

  return {
    exportUsers,
    exportInvitedUsers,
    exportGroups,
    exportOrphanedWorkspaces,
    isExporting,
  };
};

export default useUserManagementExport;
